import React, { useId, useRef } from 'react';
import { Transition } from '@headlessui/react';
import { TicketKey } from 'flow/types/EntityKeys';
import useAppContext from 'flow/hooks/useAppContext';
import Icon from 'flow/components/ui-components/Icon';
import IconButton from 'flow/components/ui-components/IconButton';

import TabsTicket, { TicketPageTabKey } from './TabsTicket';
import TicketSideTabContent from './TicketSideTabContent';

const DRAWER_RELATED_SCROLL_ID = 'ticket-drawer-related-scroll';

// The docked panel's width. Ticket.tsx adds it to the narrowest usable main
// pane to decide whether the panel fits. Keep it in sync with the
// `w-[360px] min-w-[360px]` classes below (Tailwind needs them literal).
export const DRAWER_WIDTH = 360;

// Side tabs only — the conversation always stays in the main pane. Same
// order as the main-pane row, minus the conversation.
const SIDE_TAB_KEYS: TicketPageTabKey[] = [
    'TICKET_DETAILS',
    'CONTACT',
    'RELATED',
];

interface Props {
    ticketKey: TicketKey;
    selectedTab: TicketPageTabKey;
    onSelectTab: (tab: TicketPageTabKey) => void;
    onClose: () => void;
    open: boolean;
    /** Receives focus once the panel has finished closing. */
    returnFocusTo?: React.RefObject<HTMLElement>;
}

/**
 * Docked right details panel — old-style bordered gray panel (close +
 * "Context" header) hosting the side-tab pills + content. Animates its
 * width + opacity open/closed (300ms); the inner content is held at the
 * full DRAWER_WIDTH so it doesn't reflow while the outer panel collapses (the
 * outer `overflow-hidden` clips it). The `!w-0` overrides win over the
 * steady-state width during the enter/leave transitions.
 */
const TicketDetailsDrawer = ({
    ticketKey,
    selectedTab,
    onSelectTab,
    onClose,
    open,
    returnFocusTo,
}: Props) => {
    const { i18next } = useAppContext();
    const titleId = useId();
    // The close button is reached through the panel element rather than a
    // ref on IconButton, so the shared button primitives stay untouched
    // (develop has since changed them; a forwardRef here would conflict).
    const panelRef = useRef<HTMLElement | null>(null);
    const focusCloseButton = () =>
        panelRef.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const focusIsFree = () => {
        const active = document.activeElement;
        return (
            !active ||
            active === document.body ||
            active.closest('[inert]') !== null ||
            active === returnFocusTo?.current
        );
    };
    // The conversation lives in the main pane; if it's the active tab fall
    // back to Ticket details so the panel always shows a side tab.
    const sideTab =
        selectedTab === 'CONVERSATION' ? 'TICKET_DETAILS' : selectedTab;

    return (
        <Transition
            show={open}
            as="div"
            className="flex h-full w-[360px] min-w-[360px] shrink-0 flex-col overflow-hidden"
            enter="transition-all duration-300 ease-out"
            enterFrom="!w-0 !min-w-0 opacity-0"
            enterTo="opacity-100"
            leave="transition-all duration-300 ease-out"
            leaveFrom="opacity-100"
            leaveTo="!w-0 !min-w-0 opacity-0"
            // Move focus only when nothing else holds it: after the toggle
            // the user is on <body>, on the now-inert toggle (browsers differ
            // in when they blur an inert element) or still on the toggle
            // (Safari never focuses a clicked button). A panel that opens or
            // closes from a resize across the width threshold, or a click
            // into the message input during the animation, must not steal
            // focus.
            afterEnter={() => {
                if (focusIsFree()) focusCloseButton();
            }}
            afterLeave={() => {
                if (focusIsFree()) returnFocusTo?.current?.focus();
            }}
        >
            <aside
                ref={panelRef}
                aria-labelledby={titleId}
                className="flex h-full w-[360px] min-w-[360px] flex-col border-s border-solid border-gray-200 bg-gray-100"
            >
                <header className="relative flex h-16 w-full shrink-0 items-center border-b px-4 font-bold">
                    {/* No tooltipLabel: its Tooltip wrapper is an extra,
                     *  unnamed tab stop. The name comes from aria-label and
                     *  the hover hint from title. */}
                    <IconButton
                        aria-label={i18next.t('Tooltip.Dialog.Close')}
                        title={i18next.t('Tooltip.Dialog.Close')}
                        onClick={onClose}
                    >
                        <Icon iconKey="close" className="rtl:scale-x-[-1]" />
                    </IconButton>
                    <h2
                        id={titleId}
                        className="pointer-events-none absolute inset-x-0 top-1/2 m-0 -translate-y-1/2 text-center text-base font-bold"
                    >
                        {i18next.t('tickets.context.title', 'Context')}
                    </h2>
                </header>
                <div className="py-3">
                    <TabsTicket
                        ticketKey={ticketKey}
                        selectedTab={sideTab}
                        onSelectTab={onSelectTab}
                        tabKeys={SIDE_TAB_KEYS}
                    />
                </div>
                <TicketSideTabContent
                    ticketKey={ticketKey}
                    tab={sideTab}
                    relatedScrollId={DRAWER_RELATED_SCROLL_ID}
                    contentClassName="p-4"
                    inPanel={true}
                />
            </aside>
        </Transition>
    );
};

export default React.memo(TicketDetailsDrawer);
